import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AlertBox, { AlertBoxHandles } from '../../components/AlertBox/AlertBox';
import Button from '../../components/Button/Button';
import ButtonLabel from '../../components/Button/ButtonLabel';
import FormField from '../../components/FormField/FormField';
import { useControllerFlowStepsContext } from '../../hooks/ControllerFlowStepsContext';
import { checkEmailExist } from '../../services/queryController';

import { createTokenValidateEmail } from '../../services/tokenValidation';
import CheckToken from '../CheckToken';
import StepHeader from '../../components/Wizard/StepHeader';
import Alert from '../../components/Alert';
import RedirectSignIn from '../SignUpNativo/RedirectSignIn';

export interface EmailPageProps {
  allowSkipStep?: boolean;
  updateData?: boolean;
  previousPath?: number | null;
  nextPath?: number | null;
  handleChangeStep(e: number): void;
  context: any;
}

type ViewState = 'email' | 'token' | 'emailExists';

export default function EmailPage({
  nextPath,
  previousPath,
  handleChangeStep,
  context
}: EmailPageProps) {
  const { socialWithoutEmail, showErrorMessageServer } =
    useControllerFlowStepsContext();
  const {
    register,
    errors,
    watch,
    touchedFields,
    originDomainForm,
    getValues
  } = useContext<any>(context);
  const [errorRequest, setErrorRequest] = useState('');
  const [fieldValidate, setValueValidateField] = useState('');
  const [viewState, setViewState] = useState<ViewState>('email');
  const [isLoading, setIsLoading] = useState(false);
  const url = `/${originDomainForm}/cadastro-email`;
  const navigate = useNavigate();

  useEffect(() => {
    navigate(url, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const subscription = watch((data: any) => {
      setValueValidateField(data.email);
    });
    return () => subscription?.unsubscribe();
  }, [watch]);

  async function callCreateToken(resend?: boolean) {
    await createTokenValidateEmail(fieldValidate);
    if (resend) openAlert();
  }

  const alertBoxRef = useRef<AlertBoxHandles>(null);
  function openAlert() {
    alertBoxRef.current?.openAlert();
  }

  async function changeToViewToken() {
    try {
      if (socialWithoutEmail) {
        await callCreateToken();
        setViewState('token');
      } else {
        checkExistEmail();
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function checkExistEmail() {
    const email = getValues('email');
    try {
      setIsLoading(true);
      const response = await checkEmailExist(email);
      if (response.exist) {
        setViewState('emailExists');
      } else {
        callCreateToken();
        setViewState('token');
      }
    } catch (error) {
      console.error(error);
      showErrorMessageServer();
    } finally {
      setIsLoading(false);
    }
  }

  function previousStep() {
    if (previousPath == 0 || previousPath) {
      handleChangeStep(previousPath);
    } else window.location.reload();
  }

  function goToRecoveryAccount() {
    navigate(
      originDomainForm === 'signup'
        ? '/recuperar-senha/token'
        : originDomainForm === 'sbt/signup'
          ? '/sbt/recuperar-senha/token'
          : '/videos/recuperar-senha/token'
    );
  }

  function backToEmailInput() {
    setViewState('email');
  }

  return (
    <>
      {viewState === 'email' && (
        <div className="card__section">
          <div className="card__top">
            <StepHeader
              title="Como você vai entrar?"
              description="Para entrar, você precisa utilizar um e-mail válido."
            />

            <div className="form-group">
              <Alert
                message={errorRequest}
                errors={errors}
                onClose={setErrorRequest}
              />

              <FormField
                name="email"
                label="qual seu e-mail?"
                type="email"
                placeholder="aquele que você mais usa"
                register={register('email')}
                error={errors}
                touchedFields={touchedFields}
                iconPrefix="email"
              />
            </div>
          </div>

          <div className="button-group">
            <Button
              className="mt-8"
              full
              onClick={changeToViewToken}
              disabled={!!errors.email || !fieldValidate.length || isLoading}
            >
              <ButtonLabel loading={isLoading}>continuar</ButtonLabel>
            </Button>

            <Button
              className="mt-16"
              customColors="btn-white"
              full
              onClick={previousStep}
              disabled={isLoading}
            >
              <ButtonLabel>voltar</ButtonLabel>
            </Button>

            <RedirectSignIn context={context} />
          </div>
        </div>
      )}

      {viewState === 'emailExists' && (
        <div className="card__section">
          <div className="card__top">
            <StepHeader
              title="Não foi dessa vez"
              description={[
                'Parece que este e-mail já está em uso.',
                'Caso você tenha esquecido a senha da sua Conta SBT, tente recuperar seu acesso.'
              ]}
            />
          </div>

          <div className="button-group">
            <Button className="mt-8" full onClick={backToEmailInput}>
              <ButtonLabel>Tentar novamente</ButtonLabel>
            </Button>

            <Button customColors="btn-ghost" full onClick={goToRecoveryAccount}>
              <ButtonLabel>Recuperar meu acesso</ButtonLabel>
            </Button>
          </div>
        </div>
      )}

      {viewState === 'token' && (
        <CheckToken
          context={context}
          goBackView={() => setViewState('email')}
          callCreateToken={callCreateToken}
          previousPath={0}
          nextPath={nextPath}
          handleChangeStep={handleChangeStep}
          isEmail
        />
      )}

      <AlertBox ref={alertBoxRef} text="código enviado com sucesso" />
    </>
  );
}
